import React, { useRef } from 'react';
import Header from '../Components/Header';

const AboutUs = () => {
    const scrollRef = useRef(null);

    const handleNextImage = () => {
        scrollRef.current.scrollBy({ left: 300, behavior: 'smooth' });
    };

    const handlePrevImage = () => {
        scrollRef.current.scrollBy({ left: -300, behavior: 'smooth' });
    };

    const buttonBaseStyle = {
        position: 'absolute',
        top: '50%',
        transform: 'translateY(-50%)',
        backgroundColor: 'transparent',
        border: 'none',
        padding: '8px',
        borderRadius: '50%',
        zIndex: 10,
        transition: 'background-color 0.3s ease',
        cursor: 'pointer'
    };
    return (
        <div className="min-h-screen bg-custom-white text-custom-blue font-sans">
            <div className="w-full px-4 sm:px-6 lg:px-16 xl:px-20 py-8 max-w-7xl mx-auto">
                {/* Header */}
                <Header />

                {/* Hero Section */}
                <header className="bg-custom-yellow py-16 relative overflow-hidden mt-6 rounded-xl shadow-lg">
                    <div className="max-w-5xl mx-auto px-4 text-center">
                        <h1 className="text-4xl md:text-5xl font-extrabold mb-4 leading-tight">
                            Discover the World with Prapancha Pravasa
                        </h1>
                        <p className="text-lg md:text-xl text-custom-blue mb-6">
                            Crafting unforgettable journeys with passion, precision, and care.
                        </p>
                        <a
                            href="#journey"
                            className="inline-block bg-custom-blue text-custom-white px-8 py-3 rounded-lg font-semibold text-lg hover:bg-opacity-90 transition duration-300"
                            aria-label="Explore Our Journey"
                        >
                            Explore Our Journey
                        </a>
                    </div>
                </header>

                <main className="max-w-5xl mx-auto px-4 py-12">
                    {/* Our Story */}
                    <section className="mb-16">
                        <h2 className="text-3xl md:text-4xl font-bold mb-6 text-center">Our Story</h2>
                        <div className="space-y-4 text-lg leading-relaxed">
                            <p>
                                At Prapancha Pravasa Tours Private Limited, based in Bengaluru, Karnataka, we are dedicated to creating exceptional travel experiences. Our mission is to connect travelers with the world’s beauty, culture, and adventure through meticulously curated journeys that are seamless, safe, and enriching.
                            </p>
                            <p>
                                Founded with a vision to make travel accessible and memorable, we specialize in personalized tours tailored to diverse interests—from cultural immersions to thrilling adventures. Our commitment to excellence ensures every journey leaves a lasting impression.
                            </p>
                        </div>
                    </section>

                    {/* Our Values */}
                    <section className="mb-16">
                        <h2 className="text-3xl md:text-4xl font-bold mb-6 text-center">Our Values</h2>
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                            <div className="p-6 bg-custom-yellow bg-opacity-10 rounded-lg shadow-md hover:shadow-lg transition-shadow duration-300">
                                <h3 className="text-xl font-semibold mb-2">Customer-Centric</h3>
                                <p>Your comfort and satisfaction are at the heart of everything we do.</p>
                            </div>
                            <div className="p-6 bg-custom-yellow bg-opacity-10 rounded-lg shadow-md hover:shadow-lg transition-shadow duration-300">
                                <h3 className="text-xl font-semibold mb-2">Sustainability</h3>
                                <p>We champion responsible travel to preserve destinations for future generations.</p>
                            </div>
                            <div className="p-6 bg-custom-yellow bg-opacity-10 rounded-lg shadow-md hover:shadow-lg transition-shadow duration-300">
                                <h3 className="text-xl font-semibold mb-2">Authenticity</h3>
                                <p>Our tours immerse you in the rich cultures and traditions of each destination.</p>
                            </div>
                            <div className="p-6 bg-custom-yellow bg-opacity-10 rounded-lg shadow-md hover:shadow-lg transition-shadow duration-300">
                                <h3 className="text-xl font-semibold mb-2">Reliability</h3>
                                <p>From planning to execution, we ensure a seamless and worry-free experience.</p>
                            </div>
                        </div>
                    </section>

                    {/* Our Journey */}
                    <section id="journey" className="mb-16">
                        <h2 className="text-3xl md:text-4xl font-bold mb-6 text-center">Our Journey</h2>
                        <p className="text-lg text-center mb-8">
                            Discover the world through our curated experiences, from vibrant cultures to breathtaking landscapes.
                        </p>

                        <div className="relative">
                            {/* Left Arrow */}
                            <button
                                style={{ ...buttonBaseStyle, left: '2%' }}
                                onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = 'rgba(0, 0, 0, 0.8)')}
                                onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = 'transparent')}
                                onClick={handlePrevImage}
                            >
                                <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 19l-7-7 7-7" />
                                </svg>
                            </button>

                            {/* Right Arrow */}
                            <button
                                style={{ ...buttonBaseStyle, right: '2%' }}
                                onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = 'rgba(0, 0, 0, 0.8)')}
                                onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = 'transparent')}
                                onClick={handleNextImage}
                            >
                                <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7" />
                                </svg>
                            </button>

                            {/* Scrollable Image Strip */}
                            <div
                                ref={scrollRef}
                                className="flex overflow-x-auto space-x-4 p-8 scrollbar-hide snap-x snap-mandatory"
                            >
                                {/* Your image cards go here (unchanged) */}
                                {/* Example: */}
                                <div className="flex-none w-64 h-64 snap-center">
                                    <img
                                        src="https://dbwr0bte83ose.cloudfront.net/aboutus/Sigandoor.jpg"
                                        alt="Sigandoor"
                                        className="w-full h-full object-cover rounded-lg shadow-md hover:scale-105 transition-transform duration-300"
                                        loading="lazy"
                                    />
                                    <p className="mt-2 text-center font-medium">Sigandoor</p>
                                </div>
                                {/* Add the rest of your image blocks here */}
                                <div className="flex-none w-64 h-64 snap-center">
                                    <img
                                        src="https://dbwr0bte83ose.cloudfront.net/aboutus/Thailand-1.jpg"
                                        alt="Thailand"
                                        className="w-full h-full object-cover rounded-lg shadow-md hover:scale-105 transition-transform duration-300"
                                        loading="lazy"
                                    />
                                    <p className="mt-2 text-center font-medium">Thailand</p>
                                </div>
                                <div className="flex-none w-64 h-64 snap-center">
                                    <img
                                        src="https://dbwr0bte83ose.cloudfront.net/aboutus/Thailand-2.png"
                                        alt="Thailand"
                                        className="w-full h-full object-cover rounded-lg shadow-md hover:scale-105 transition-transform duration-300"
                                        loading="lazy"
                                    />
                                    <p className="mt-2 text-center font-medium">Thailand</p>
                                </div>
                                <div className="flex-none w-64 h-64 snap-center">
                                    <img
                                        src="https://dbwr0bte83ose.cloudfront.net/aboutus/Thailand-3.jpg"
                                        alt="Thailand"
                                        className="w-full h-full object-cover rounded-lg shadow-md hover:scale-105 transition-transform duration-300"
                                        loading="lazy"
                                    />
                                    <p className="mt-2 text-center font-medium">Thailand</p>
                                </div>
                                <div className="flex-none w-64 h-64 snap-center">
                                    <img
                                        src="https://dbwr0bte83ose.cloudfront.net/aboutus/Thailand-4.jpg"
                                        alt="Thailand"
                                        className="w-full h-full object-cover rounded-lg shadow-md hover:scale-105 transition-transform duration-300"
                                        loading="lazy"
                                    />
                                    <p className="mt-2 text-center font-medium">Thailand</p>
                                </div>
                                <div className="flex-none w-64 h-64 snap-center">
                                    <img
                                        src="https://dbwr0bte83ose.cloudfront.net/aboutus/Yana.jpg"
                                        alt="Yana"
                                        className="w-full h-full object-cover rounded-lg shadow-md hover:scale-105 transition-transform duration-300"
                                        loading="lazy"
                                    />
                                    <p className="mt-2 text-center font-medium">Yana</p>
                                </div>
                                <div className="flex-none w-64 h-64 snap-center">
                                    <img
                                        src="https://dbwr0bte83ose.cloudfront.net/aboutus/Chikmanglore.jpg"
                                        alt="Chikmanglore"
                                        className="w-full h-full object-cover rounded-lg shadow-md hover:scale-105 transition-transform duration-300"
                                        loading="lazy"
                                    />
                                    <p className="mt-2 text-center font-medium">Chikmanglore</p>
                                </div>
                            </div>
                        </div>
                    </section>

                    {/* Contact Us */}
                    <section className="mb-16">
                        <h2 className="text-3xl md:text-4xl font-bold mb-6 text-center">Contact Us</h2>
                        <p className="text-lg text-center mb-6">
                            Ready to plan your next adventure? Our team is here to assist you.
                        </p>
                        <div className="text-center space-y-2">
                            <p><strong>Address:</strong> 1st Floor, 603, 1st Main Road, 2nd Cross Rd, near Jain Temple, T. Dasarahalli, Bengaluru, Karnataka 560057</p>
                            <p><strong>Email:</strong> <a href="mailto:book@prapanchapravasa.com" className="underline hover:text-custom-yellow transition-colors duration-200">book@prapanchapravasa.com</a></p>
                            <p><strong>Phone:</strong> <span className="hover:text-custom-yellow transition-colors duration-200">63602 04100, 78997 30480,
                                72042 47400, 82964 63588</span></p>
                        </div>
                    </section>

                    {/* Find Us */}
                    <section className="mb-16">
                        <h2 className="text-3xl md:text-4xl font-bold mb-6 text-center">Find Us</h2>
                        <div className="w-full h-96 rounded-lg shadow-lg overflow-hidden">
                            <iframe
                                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3886.8458560331405!2d77.51385069999999!3d13.045481899999999!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x3bae23c628d27f0f%3A0xcf59f2771ccba7f6!2sPrapancha%20Pravasa%20Tours%20Private%20Limited!5e0!3m2!1sen!2sin!4v1778062992848!5m2!1sen!2sin"
                                width="100%"
                                height="100%"
                                style={{ border: 0 }}
                                allowFullScreen=""
                                loading="lazy"
                                referrerPolicy="no-referrer-when-downgrade"
                                title="Prapancha Pravasa Location"
                            ></iframe>
                        </div>
                    </section>
                </main>

                <footer className="bg-custom-blue text-custom-white py-6 text-center">
                    <p className="text-sm">
                        &copy; 2025 Prapancha Pravasa Tours Private Limited. All rights reserved.
                    </p>
                </footer>
            </div>
        </div>
    );
};

export default AboutUs;